import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  TopToolbar,
  CreateButton,
  ExportButton,
  ReferenceField,
  FunctionField,
  useRedirect,
} from 'react-admin';
import { Chip, Box } from '@mui/material';
import { RequestQuote as EstimateIcon } from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

interface EstimateRecord {
  status?: string;
}

const StatusChip = ({ record }: { record: EstimateRecord }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={record.status?.toUpperCase() || 'UNKNOWN'}
      color={
        getStatusColor(record.status || '') as
          | 'default'
          | 'primary'
          | 'secondary'
          | 'error'
          | 'info'
          | 'success'
          | 'warning'
      }
      size="small"
    />
  );
};

export const EstimateList = () => {
  const redirect = useRedirect();

  const handleCreateEstimate = () => {
    redirect('/estimates/create');
  };

  return (
    <Box>
      <PageTitle
        title="Estimates"
        titleKey="pages.estimates"
        subtitle="Create and manage project estimates"
        icon={<EstimateIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        empty={<CustomEmptyState resource="estimates" onAction={handleCreateEstimate} />}
        sx={{ '& .RaList-main': { mt: 0 } }}
      >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="estimateNumber" label="Number" />
        <TextField
          source="customer.displayName"
          label="Customer"
          emptyText="Unknown Customer"
        />
        <NumberField source="totalAmount" options={{ style: 'currency', currency: 'USD' }} />
        <FunctionField
          source="status"
          label="Status"
          render={(record: EstimateRecord) => <StatusChip record={record} />}
        />
        <DateField source="createdAt" label="Created" showTime />
      </Datagrid>
    </List>
    </Box>
  );
};
